import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Navbar from '../components/common/Navbar';
import api from '../utils/api';
import toast from 'react-hot-toast';

const POLL_INTERVAL = 8000;

function formatTimer(ms) {
  if (ms <= 0) return '00:00';
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const STATUS_LABEL = {
  pending: 'Menunggu Pembayaran',
  paid: 'Pembayaran Berhasil ✓',
  failed: 'Pembayaran Gagal',
  expired: 'Kedaluwarsa',
};

const STATUS_COLOR = {
  pending: 'text-yellow-700 bg-yellow-50 border-yellow-200',
  paid: 'text-green-700 bg-green-50 border-green-200',
  failed: 'text-red-600 bg-red-50 border-red-200',
  expired: 'text-gray-500 bg-gray-100 border-gray-200',
};

export default function CheckoutPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrData, setQrData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(300000);

  // Fetch order + QR data
  const fetchAll = useCallback(async () => {
    try {
      const [orderRes, qrRes] = await Promise.all([
        api.get(`/api/orders/${orderId}`),
        api.get(`/api/orders/${orderId}/qrcode`).catch(() => null),
      ]);
      setOrder(orderRes.data);
      if (qrRes?.data?.qris_code) {
        setQrData(qrRes.data);
        if (qrRes.data.expires_at) {
          const ms = new Date(qrRes.data.expires_at).getTime() - Date.now();
          setTimeLeft(ms > 0 ? ms : 0);
        }
      }
      return orderRes.data.status;
    } catch {
      toast.error('Gagal memuat order');
      return null;
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft <= 0) return;
    const t = setInterval(() => setTimeLeft(p => p - 1000), 1000);
    return () => clearInterval(t);
  }, [timeLeft]);

  // Initial fetch + poll
  useEffect(() => {
    fetchAll();
    const timer = setInterval(async () => {
      try {
        // Poll via check-qr endpoint
        const res = await api.post(`/api/orders/${orderId}/check-qr`);
        if (res.data.status === 'paid' || res.data.status === 'failed' || res.data.status === 'expired') {
          setOrder(prev => prev ? { ...prev, status: res.data.status } : prev);
          clearInterval(timer);
        }
      } catch {
        // fallback: fetch full order
        const s = await fetchAll();
        if (s === 'paid' || s === 'failed' || s === 'expired') clearInterval(timer);
      }
    }, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [orderId, fetchAll]);

  if (loading) {
    return (
      <div className="flex flex-col h-screen bg-wa-bg">
        <Navbar title="Checkout" />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-400">Memuat order...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex flex-col h-screen bg-wa-bg">
        <Navbar title="Checkout" />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-red-400">Order tidak ditemukan</p>
        </div>
      </div>
    );
  }

  const status = order.status || 'pending';
  const isExpired = timeLeft <= 0 && status === 'pending';

  return (
    <div className="flex flex-col h-screen bg-wa-bg">
      <Navbar title="Checkout" />
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-md overflow-hidden">
          {/* Header */}
          <div className="bg-wa-dark px-5 py-4">
            <h2 className="text-white font-bold text-lg">Ringkasan Pesanan</h2>
            <p className="text-green-200 text-xs font-mono mt-0.5">#{order.id}</p>
          </div>

          <div className="p-5 space-y-4">
            {/* Order details */}
            <div className="border rounded-lg divide-y text-sm">
              <div className="flex justify-between px-4 py-2">
                <span className="text-gray-500">Ebook</span>
                <span className="font-medium text-wa-text">{order.ebook_title}</span>
              </div>
              <div className="flex justify-between px-4 py-2">
                <span className="text-gray-500">Penjual</span>
                <span className="text-gray-700">{order.seller_name || '–'}</span>
              </div>
              <div className="flex justify-between px-4 py-2">
                <span className="text-gray-500">Total</span>
                <span className="font-bold text-wa-dark text-base">
                  Rp {Number(order.amount || 0).toLocaleString('id-ID')}
                </span>
              </div>
            </div>

            {/* Status */}
            <div className={`border rounded-lg px-4 py-2 text-sm font-medium text-center ${STATUS_COLOR[status]}`}>
              {STATUS_LABEL[status] || status}
              {status === 'pending' && (
                <span className="ml-2 inline-block w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
              )}
            </div>

            {/* QRIS */}
            {status === 'pending' && qrData?.qris_code && !isExpired && (
              <div className="flex flex-col items-center gap-3">
                <div className="bg-white p-4 rounded-xl border-2 border-gray-200 shadow-sm">
                  <QRCodeSVG
                    value={qrData.qris_code}
                    size={220}
                    level="M"
                    includeMargin={false}
                    imageSettings={{
                      src: '',
                      height: 0,
                      width: 0,
                    }}
                  />
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Scan QRIS ini dengan DANA / Mobile Banking</p>
                  <p className="text-sm font-semibold text-wa-dark mt-1">
                    Rp {Number(qrData.amount || order.amount || 0).toLocaleString('id-ID')}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Batas waktu:</span>
                  <span className={`font-mono font-bold ${timeLeft < 60000 ? 'text-red-500' : 'text-yellow-600'}`}>
                    {formatTimer(timeLeft)}
                  </span>
                </div>
                <p className="text-xs text-gray-400 text-center">
                  Halaman otomatis update setelah pembayaran berhasil
                </p>
              </div>
            )}

            {/* Expired */}
            {status === 'pending' && isExpired && (
              <div className="text-center py-4">
                <p className="text-red-500 font-medium">QRIS sudah kedaluwarsa</p>
                <p className="text-xs text-gray-400 mt-1">Silakan buat order baru</p>
              </div>
            )}

            {/* No QR data fallback */}
            {status === 'pending' && !qrData?.qris_code && !isExpired && (
              <div className="flex flex-col items-center gap-3">
                <div className="w-44 h-44 bg-gray-100 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center">
                  <span className="text-5xl">📱</span>
                  <span className="text-xs text-gray-400 mt-2 text-center">Memuat QRIS...</span>
                </div>
              </div>
            )}

            {status === 'paid' && (
              <div className="text-center py-2">
                <span className="text-5xl">🎉</span>
                <p className="text-green-700 font-semibold mt-2">Pembayaran berhasil!</p>
                <button
                  onClick={() => navigate('/library')}
                  className="mt-3 bg-wa-green hover:bg-green-500 text-white text-sm font-semibold px-6 py-2 rounded-full transition"
                >
                  Lihat Perpustakaan
                </button>
              </div>
            )}

            {(status === 'failed' || status === 'expired') && (
              <div className="text-center py-2">
                <p className="text-red-500 font-medium">Pembayaran tidak berhasil.</p>
                <button
                  onClick={() => navigate('/')}
                  className="mt-3 border border-gray-300 text-gray-600 text-sm px-6 py-2 rounded-full hover:bg-gray-50 transition"
                >
                  Kembali ke Katalog
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
